"use client"

import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useSession } from "next-auth/react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { StarRating } from "@/components/star-rating"
import { MessageSquare, Trash2, Edit, MoreVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatDistanceToNow } from "date-fns"

interface Review {
  id: string
  rating: number
  comment?: string | null
  createdAt: string
  updatedAt: string
  user: {
    id: string
    name?: string | null
    image?: string | null
  }
}

interface ReviewsListProps {
  bookId: string
  onEdit?: (review: Review) => void
}

export function ReviewsList({ bookId, onEdit }: ReviewsListProps) {
  const { data: session } = useSession()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const limit = 10

  const { data, isLoading, error } = useQuery<{
    reviews: Review[]
    pagination: {
      page: number
      limit: number
      total: number
      pages: number
    }
    stats: {
      averageRating: number
      totalReviews: number
    }
  }>({
    queryKey: ["reviews", bookId, page],
    queryFn: async () => {
      const response = await fetch(`/api/books/${bookId}/reviews?page=${page}&limit=${limit}`)
      if (!response.ok) {
        throw new Error("Failed to fetch reviews")
      }
      return response.json()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/books/${bookId}/reviews`, {
        method: "DELETE",
      })
      if (!response.ok) {
        throw new Error("Failed to delete review")
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews", bookId] })
    },
  })

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete your review?")) {
      return
    }
    deleteMutation.mutate()
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-6">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/6 mb-4" />
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-destructive text-center">
            Failed to load reviews. Please try again.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.reviews.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">
              No reviews yet. Be the first to review this book!
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {data.reviews.map((review) => {
        const userId = session?.user ? (session.user as { id?: string })?.id : undefined
        const isOwnReview = userId === review.user.id
        const initials = review.user.name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2) || "U"

        return (
          <Card key={review.id} className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={review.user.image || ""} alt={review.user.name || ""} />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm">
                        {review.user.name || "Anonymous"}
                      </p>
                      {isOwnReview && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          You
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <StarRating rating={review.rating} size="sm" />
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                        {review.updatedAt !== review.createdAt && " (edited)"}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {review.comment}
                      </p>
                    )}
                  </div>
                </div>
                {isOwnReview && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onEdit?.(review)}
                        className="cursor-pointer"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleDelete}
                        className="cursor-pointer text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}

      {/* Pagination */}
      {data.pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {data.pagination.page} of {data.pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(data.pagination.pages, p + 1))}
            disabled={page === data.pagination.pages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

